import React, { useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { red, orange, green, indigo, deepPurple, blue, amber } from "@mui/material/colors";
import { alpha, useTheme } from "@mui/material/styles";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import BookmarkAddRoundedIcon from "@mui/icons-material/BookmarkAddRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import RuleRoundedIcon from "@mui/icons-material/RuleRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";

import Header from "./header";
import Footer from "./footer";
import BannerComponent from "./components/banner";
import NoticeComponent from "./components/noticeComponent";
import GuidelineComponent from "./components/guidelines";
import AcademicSection from "./components/academic_section";
import QuestionAnswers from "./question_ans";

import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
  Divider,
  Tooltip, Chip, CardActionArea,
  Tabs, Tab,
  Stack, Button, ButtonBase, Rating, LinearProgress,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SyllabusSection from "./components/syllabus_component";
import Seo from "./components/Seo";
import seoRoutes from "../seo/seoRoutes";

const ORANGE_ACCENT = orange?.A400 || orange[600] || "#ff9100";

/* ---------- helpers ---------- */
 function SectionHeader({
  kicker,
  title,
  subtitle,
  align = "left",
  compact = false,
  action = null,
  sx = {},
}) {
  const center = align === "center";

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      spacing={1.25}
      sx={{ mb: compact ? 1.25 : 2.25, ...sx }}
    >
      <Box sx={{ textAlign: center ? "center" : "left", flex: 1, minWidth: 0 }}>
        {kicker && (
          <Box
            sx={{
              display: "inline-flex",
              px: 1,
              py: 0.5,
              borderRadius: 999,
              bgcolor: alpha(ORANGE_ACCENT, 0.18),
              color: orange[800] || "#ef6c00",
              fontWeight: 900,
              fontSize: { xs: 11.5, sm: 12.5 },
              letterSpacing: 0.3,
            }}
          >
            {kicker}
          </Box>
        )}

        {title && (
          <Typography
            component="h2"
            sx={{
              mt: 1,
              fontWeight: 900,
              lineHeight: 1.18,
              fontSize: { xs: 20, sm: 22, md: 24 },
              background: `#000`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
        )}

        {subtitle && (
          <Typography sx={{ mt: 0.5, color: "text.secondary", fontSize: { xs: 13, sm: 13.5 } }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Optional right-side action (e.g., “See all”) */}
      {action}
    </Stack>
  );
}

// add this util (same as in details page)
const slugify = (s = "") =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/* ---------- sample data (safe defaults) ---------- */
const courseData = [
  {
    image: "assets/images/course/science.png",
    courseName: "Language",
    stream: "Arts",
    courseDesc: ["English (Compulsory)", "Hindi"],
  },
  {
    image: "assets/images/course/science.png",
    courseName: "Science",
    stream: "Science",
    courseDesc: [
      "PCMB (Physics, Chemistry, Mathematics, Biology)",
      "PCMC (Physics, Chemistry, Mathematics, Computer Science)",
      "PCME (Physics, Chemistry, Mathematics, Electronics)",
    ],
  },
  {
    image: "assets/images/course/commerce.jpg",
    courseName: "Commerce",
    stream: "Commerce",
    courseDesc: [
      "CAMS (Business Studies, Accountancy, Mathematics, Statistics)",
      "CAME (Business Studies, Accountancy, Mathematics, Economics)",
    ],
  },
  {
    image: "assets/images/course/arts.jpg",
    courseName: "Arts",
    stream: "Arts",
    courseDesc: [
      "PPES (Psychology, Political Science, Economics, Sociology)",
      "HESP (History, Economics, Sociology, Political Science)",
      "HEPP (History, Economics, Political Science, Psychology)",
    ],
  },
];

function StarBar({ stars, count, total, color = red[600] }) {
  const pct = total ? (count / total) * 100 : 0;
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 0.25 }}>
      <Typography sx={{ width: 18, fontWeight: 800, fontSize: 12 }}>{stars}</Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 6,
          backgroundColor: alpha(color, 0.12),
          "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 6 },
        }}
      />
      <Typography sx={{ width: 36, textAlign: "right", color: "text.secondary", fontSize: 12 }}>
        {count}
      </Typography>
    </Stack>
  );
}

function HomePage() {
  const syllabusRef = useRef(null);
const BRAND = { base: red[600], dark: red[700], on: "#fff" };
const theme = useTheme();
const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
const [activitiesTab, setActivitiesTab] = React.useState(0);
const ORANGE_ACCENT = orange?.A400 || orange[600] || "#ff9100";
const STREAM_COLORS = {
  all: BRAND.base,
  science: green[600],
  commerce: indigo[600],
  arts: deepPurple[600],
};
const getStreamColor = (s) => STREAM_COLORS[(s || "all").toLowerCase()] || BRAND.base;

// bookmarks (optional)
const [saved, setSaved] = React.useState(() => new Set());
const toggleSaved = (i) =>
  setSaved(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

// stream filter
const [selectedStream, setSelectedStream] = React.useState("All");
const filteredCourses =
  selectedStream === "All"
    ? courseData
    : courseData.filter(c => (c.stream || "").toLowerCase() === selectedStream.toLowerCase());

    const reviewStats = {
    average: 4.6,
    total: 128,
    breakdown: { 5: 86, 4: 28, 3: 8, 2: 3, 1: 3 },
    recommendPct: 97,
  };

//   const PRIMARY = blue[700];        // #1976d2
// const PRIMARY_DARK = blue[800];   // #1565c0
// const ACCENT = amber[600];        // #ffb300
const seo = seoRoutes["/"];
  return (
    <>
     <Seo
      title={seo.title}
      description={seo.description}
      path="/"
      image={seo.image}
    />
      <Header />

      {/* Banner */}
      <Box sx={{ pt: 0 /* offset fixed header */, mb: 3 }}>
        <BannerComponent />
      </Box>
 {/* Leadership blurbs (compact) */}
<Container
      maxWidth="lg"
      sx={{
        mt: { xs: 2, md: -6 }, // overlap hero on desktop (tweak or remove)
        mb: { xs: 4, md: 6 },
        position: "relative",
        zIndex: 2,
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 2.5 }} alignItems="stretch">
        {/* Principal’s Message */}
        <Grid item xs={12} md={7}>
          <Card
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              p: { xs: 1.75, sm: 2.25 },
              borderRadius: 3,
              height: "100%",
              display: "flex",
              gap: { xs: 1.75, sm: 2.25 },
              alignItems: "flex-start",
              boxShadow: "0 10px 30px rgba(0,0,0,.10)",
              border: `1px solid ${alpha(blue[700], 0.22)}`,
              // Subtle, friendly gradient
              backgroundImage: `linear-gradient(135deg, ${alpha(
                blue[700],
                0.10
              )} 0%, ${alpha(amber[600], 0.12)} 100%)`,
              // Dotted overlay
              "&:before": {
                content: '""',
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(rgba(255,255,255,.18) 1px, transparent 1.5px)",
                backgroundSize: "14px 14px",
                pointerEvents: "none",
              },
              // Left accent bar
              "&:after": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                background: `linear-gradient(180deg, ${blue[700]}, ${amber[600]})`,
              },
            }}
          >
            <Box
              component="img"
              src="/assets/images/team/principal.jpeg"
              alt="Smt. Mamta Sharma"
              loading="lazy"
              sx={{
                width: { xs: 96, sm: 108 },
                height: { xs: 96, sm: 108 },
                borderRadius: 2,
                objectFit: "cover",
                flexShrink: 0,
                boxShadow: "0 12px 24px rgba(0,0,0,.18)",
                border: "3px solid rgba(255,255,255,.85)",
                backgroundColor: "#fff",
              }}
            />

            <Box sx={{ minWidth: 0, flex: 1, position: "relative", zIndex: 1 }}>
              {/* Badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  mb: 0.75,
                  bgcolor: alpha(blue[700], 0.12),
                  color: blue[800],
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0.2,
                }}
              >
                Principal’s Message
              </Box>

              {/* Title */}
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: 18, sm: 19 },
                  mb: 0.25,
                  lineHeight: 1.25,
                  background: `#000000`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                From Principal&apos;s Desk
              </Typography>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: 14, sm: 14.5 },
                  mb: 1,
                }}
              >
                Smt. Mamta Sharma
              </Typography>

              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: 14, sm: 14.5 },
                  lineHeight: 1.7,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                It is with great pride and enthusiasm that I welcome you to our
                college in its 3rd year of establishment. Though still young,
                the institution has already begun to make a mark through a
                commitment to academic excellence, holistic development, and
                community engagement.
              </Typography>

              <Button
                component={NavLink}
                to="/principalmessage"
                size="small"
                sx={{
                  mt: 1,
                  px: 0,
                  textTransform: "none",
                  fontWeight: 800,
                  color: blue[800],
                  "&:hover": {
                    color: amber[700],
                    backgroundColor: "transparent",
                  },
                }}
              >
                Read more →
              </Button>
            </Box>

            {/* Watermark */}
            <FormatQuoteRoundedIcon
              sx={{
                position: "absolute",
                right: { xs: -6, sm: -8 },
                bottom: { xs: -10, sm: -12 },
                fontSize: { xs: 120, sm: 140 },
                color: alpha(blue[900], 0.08),
                transform: "rotate(180deg)",
                pointerEvents: "none",
              }}
            />
          </Card>
        </Grid>

        {/* About College */}
        <Grid item xs={12} md={5}>
          <Card
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              p: { xs: 1.75, sm: 2.25 },
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 10px 30px rgba(0,0,0,.10)",
              border: `1px solid ${alpha(blue[700], 0.22)}`,
              backgroundImage: `linear-gradient(135deg, ${alpha(
                blue[700],
                0.10
              )} 0%, ${alpha(amber[600], 0.12)} 100%)`,
              "&:before": {
                content: '""',
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(rgba(255,255,255,.16) 1px, transparent 1.5px)",
                backgroundSize: "14px 14px",
                pointerEvents: "none",
              },
              // Top accent strip
              "&:after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                height: 4,
                background: `linear-gradient(90deg, ${blue[700]}, ${amber[600]})`,
              },
            }}
          >
            {/* Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                mb: 0.75,
                bgcolor: alpha(blue[700], 0.12),
                color: blue[800],
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.2,
              }}
            >
              <SchoolRoundedIcon sx={{ fontSize: 16 }} />
              About Us
            </Box>

            {/* Title */}
            <Typography
              sx={{
                fontWeight: 900,
                mb: 1,
                fontSize: { xs: 18, sm: 19 },
                lineHeight: 1.25,
                background: `#000`,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              About AISECT Inter College
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: { xs: 14, sm: 14.5 },
                lineHeight: 1.8,
              }}
            >
              Established in 2023 at Matwari, Hazaribagh (Jharkhand), AIU
              delivers quality intermediate education across Science, Arts, and
              Commerce with a learner-centric approach and modern infrastructure.
            </Typography>

            {/* Quick stats */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, 1fr)" },
                gap: 1.25,
                mt: 2,
              }}
            >
              {[
                {
                  k: "Streams",
                  v: "Science · Commerce · Arts",
                  icon: (
                    <TimelineRoundedIcon
                      sx={{ fontSize: 18, color: blue[700] }}
                    />
                  ),
                },
                {
                  k: "Founded",
                  v: "2023",
                  icon: (
                    <SchoolRoundedIcon
                      sx={{ fontSize: 18, color: amber[700] }}
                    />
                  ),
                },
              ].map((s) => (
                <Box
                  key={s.k}
                  sx={{
                    p: 1.1,
                    borderRadius: 2,
                    bgcolor: alpha("#fff", 0.7),
                    backdropFilter: "saturate(140%) blur(4px)",
                    border: (t) => `1px dashed ${alpha(t.palette.divider, 0.8)}`,
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(blue[700], 0.12),
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>
                      {s.k}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: 13.5 }}>
                      {s.v}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ mt: "auto" }}>
              <Button
                component={NavLink}
                to="/aboutcollege"
                size="small"
                sx={{
                  mt: 1.75,
                  textTransform: "none",
                  fontWeight: 800,
                  px: 0,
                  color: blue[800],
                  "&:hover": {
                    color: amber[700],
                    backgroundColor: "transparent",
                  },
                }}
              >
                Read more →
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>


      {/* Popular Courses */}
      <Container maxWidth="lg" sx={{ mb: { xs: 3, sm: 4 } }}>
  {/* Header row with colored kicker + title + stream filter */}
  <Box
    sx={{
      mb: { xs: 2, sm: 2.5 },
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      gap: 2,
      flexWrap: "wrap",
    }}
  >
    {/* Left: Kicker + Title with new colors */}
    <Box>
      {/* Kicker: "Popular Courses" (colored) */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1,
          py: 0.5,
          borderRadius: 999,
          bgcolor: alpha(ORANGE_ACCENT, 0.15),
          color: ORANGE_ACCENT,
          fontWeight: 800,
          fontSize: 12.5,
          letterSpacing: 0.3,
          mb: 0.75,
        }}
      >
        Popular Courses
      </Box>

      {/* Title: gradient brand text */}
      <Typography
        sx={{
          fontSize: { xs: 20, sm: 22 },
          fontWeight: 900,
          lineHeight: 1.2,
          background: `#000`,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}
      >
        Explore Our Streams
      </Typography>

      {/* Count (optional) */}
      <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: 13 }}>
        Showing {filteredCourses.length} {filteredCourses.length === 1 ? "course" : "courses"}
        {selectedStream !== "All" ? ` in ${selectedStream}` : ""}
      </Typography>
    </Box>

    {/* Right: Stream filter chips */}
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
      {["All", "Science", "Commerce", "Arts"].map((s) => {
        const active = selectedStream === s;
        const c = getStreamColor(s);
        return (
          <ButtonBase
            key={s}
            // to={`/courses/${slugify(s.courseName)}`}
            onClick={() => setSelectedStream(s)}
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              fontWeight: 800,
              fontSize: 12.5,
              border: `1px solid ${alpha(c, 0.4)}`,
              color: active ? "#fff" : c,
              bgcolor: active ? c : alpha(c, 0.06),
              transition: "all .2s ease",
              "@media (hover: hover) and (pointer: fine)": {
                "&:hover": { bgcolor: active ? c : alpha(c, 0.12) },
              },
            }}
            aria-label={`Filter by ${s}`}
          >
            {s}
          </ButtonBase>
        );
      })}
    </Stack>
  </Box>

  {/* Courses grid */}
  <Grid
    container
    spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
    columns={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
  >
    {filteredCourses.map((item, idx) => {
      const titleId = `course-title-${idx}`;
      const streamColor = getStreamColor(item.stream);
      return (
        <Grid key={idx} item xs={12} sm={6} md={4} lg={3}>
          <Card
            role="article"
            aria-labelledby={titleId}
            sx={{
              position: "relative",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              borderRadius: { xs: 2, md: 3 },
              bgcolor: "background.paper",
              overflow: "hidden",
              boxShadow: "0 8px 24px rgba(0,0,0,.08)",
              // gradient ring
              "&:before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: { xs: 8, md: 12 },
                padding: "1px",
                background: `linear-gradient(135deg, ${alpha(red[600], 0.6)}, ${alpha(
                  ORANGE_ACCENT,
                  0.7
                )})`,
                WebkitMask:
                  "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                pointerEvents: "none",
              },
              transition: "transform .25s ease, box-shadow .25s ease",
              "@media (hover: hover) and (pointer: fine)": {
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 16px 36px rgba(0,0,0,.14)",
                },
              },
            }}
          >
            <CardActionArea component={Link} to={`/courses/${slugify(item.courseName)}`} sx={{ alignItems: "stretch" }}>
              <Box sx={{ position: "relative" }}>
                <CardMedia
                  component="img"
                  src={item.image}
                  alt={`${item.courseName} image`}
                  loading="lazy"
                  sizes="(max-width:600px) 100vw, (max-width:1200px) 50vw, 25vw"
                  sx={{
                    aspectRatio: "16/10",
                    width: "100%",
                    objectFit: "cover",
                    transform: "scale(1)",
                    transition: "transform .6s cubic-bezier(.2,.6,.2,1)",
                    "@media (hover: hover) and (pointer: fine)": {
                      ".MuiCard-root:hover &": { transform: "scale(1.06)" },
                    },
                  }}
                />

                {/* Stream badge (colored by stream) */}
                {(item.stream || item.tag) && (
                  <Chip
                    size="small"
                    icon={<StarRoundedIcon sx={{ fontSize: 16, color: streamColor }} />}
                    label={item.stream || item.tag}
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      bgcolor: alpha("#fff", 0.92),
                      backdropFilter: "saturate(140%) blur(4px)",
                      fontWeight: 800,
                      color: streamColor,
                      border: `1px solid ${alpha(streamColor, 0.4)}`,
                      maxWidth: { xs: "88%", sm: "75%" },
                    }}
                  />
                )}

                {/* bottom gradient fade */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "36%",
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.45) 100%)",
                    pointerEvents: "none",
                  }}
                />
              </Box>
            </CardActionArea>

            <CardContent
              sx={{
                p: { xs: 1.25, sm: 1.5, md: 1.75 },
                pt: { xs: 1, sm: 1.25 },
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                gap: { xs: 0.75, sm: 1 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  id={titleId}
                  component={Link}
                  to={`/courses/${slugify(item.courseName)}`}
                  style={{ textDecoration: "none" }}
                  sx={{
                    color: "text.primary",
                    fontWeight: 900,
                    letterSpacing: 0.2,
                    fontSize: { xs: 15, sm: 16, md: 16.5 },
                    lineHeight: 1.25,
                    background: `linear-gradient(90deg, ${red[700]} 0%, ${ORANGE_ACCENT} 100%)`,
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    transition: "opacity .2s ease",
                    "@media (hover: hover) and (pointer: fine)": {
                      "&:hover": { opacity: 0.85 },
                    },
                  }}
                >
                  {item.courseName}
                </Typography>

                {/* Bookmark (doesn't navigate) */}
                <Tooltip title={saved.has(idx) ? "Remove bookmark" : "Bookmark"}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSaved(idx);
                    }}
                    sx={{
                      ml: "auto",
                      borderRadius: 1.5,
                      p: { xs: 0.75, sm: 0.5 },
                      minWidth: 40,
                      minHeight: 40,
                      bgcolor: "transparent",
                      color: saved.has(idx) ? red[600] : "text.secondary",
                      transition: "all .2s ease",
                      "@media (hover: hover) and (pointer: fine)": {
                        "&:hover": {
                          bgcolor: alpha(ORANGE_ACCENT, 0.12),
                          color: ORANGE_ACCENT,
                        },
                      },
                    }}
                    aria-label={`Bookmark ${item.courseName}`}
                  >
                    {saved.has(idx) ? (
                      <BookmarkRoundedIcon sx={{ fontSize: { xs: 20, sm: 18 } }} />
                    ) : (
                      <BookmarkAddRoundedIcon sx={{ fontSize: { xs: 20, sm: 18 } }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Bullets */}
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {item.courseDesc.map((d, i) => (
                  <Typography
                    key={i}
                    component="li"
                    sx={{
                      fontSize: { xs: 12.5, sm: 13 },
                      color: "text.secondary",
                      lineHeight: 1.55,
                      mb: 0.5,
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      WebkitLineClamp: { xs: 2, sm: 3 },
                    }}
                  >
                    {d}
                  </Typography>
                ))}
              </Box>

              {/* Optional tags row */}
              {Array.isArray(item.tags) && item.tags.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {item.tags.slice(0, 3).map((t, i) => (
                    <Chip
                      key={i}
                      label={t}
                      size="small"
                      sx={{
                        bgcolor: alpha(streamColor, 0.08),
                        color: streamColor,
                        fontWeight: 700,
                        border: `1px solid ${alpha(streamColor, 0.25)}`,
                      }}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      );
    })}
  </Grid>
</Container>



      {/* Activities: Notices / Guidelines / Syllabus */}
      {/* <Container maxWidth="lg" sx={{ mb: 4 }}>
        <SectionHeader kicker="Activities" title="Stay Updated" compact />
        <Grid container spacing={{ xs: 2, sm: 2.5 }}>
          <Grid item xs={12} md={6} lg={4}>
            <NoticeComponent />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <GuidelineComponent />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Syllabus syllabusRef={syllabusRef} />
          </Grid>
        </Grid>
      </Container> */}
{/* === Activities Section === */}
<Container maxWidth="lg" sx={{ mb: 4 }}>
  <Box
    sx={{
      position: "relative",
      overflow: "hidden",
      p: { xs: 1.25, sm: 1.75, md: 2 },
      borderRadius: 3,
      backgroundImage: `linear-gradient(135deg, ${alpha(red[600], 0.10)} 0%, ${alpha(
        ORANGE_ACCENT,
        0.10
      )} 100%)`,
      border: `1px solid ${alpha(red[600], 0.22)}`,
      boxShadow: "0 10px 30px rgba(0,0,0,.10)",
      "&:before": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage:
          "radial-gradient(rgba(255,255,255,.16) 1px, transparent 1.5px)",
        backgroundSize: "14px 14px",
        pointerEvents: "none",
      },
    }}
  >
    {/* Header */}
    <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.35,
          borderRadius: 999,
          bgcolor: alpha(ORANGE_ACCENT, 0.16),
          color: orange[700] || "#f57c00",
          fontWeight: 900,
          fontSize: 12.5,
          letterSpacing: 0.3,
          mb: 0.5,
        }}
      >
        Activities
      </Box>

      <Typography
        sx={{
          fontSize: { xs: 20, sm: 22 },
          fontWeight: 900,
          lineHeight: 1.2,
          background: `#000`,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}
      >
        Stay Updated
      </Typography>

      {/* Quick actions */}
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
        <Button
          component={Link}
          to="/notice"
          size="small"
          variant="outlined"
          startIcon={<CampaignRoundedIcon />}
          endIcon={<ChevronRightRoundedIcon />}
          sx={{
            borderRadius: 2,
            borderColor: alpha(red[600], 0.5),
            color: red[700],
            "&:hover": { borderColor: red[600], bgcolor: alpha(red[600], 0.06) },
          }}
        >
          All Notices
        </Button>
        <Button
          component={Link}
          to="/guidelines"
          size="small"
          variant="outlined"
          startIcon={<RuleRoundedIcon />}
          endIcon={<ChevronRightRoundedIcon />}
          sx={{
            borderRadius: 2,
            borderColor: alpha(orange[700] || "#f57c00", 0.5),
            color: orange[800] || "#ef6c00",
            "&:hover": {
              borderColor: orange[700] || "#f57c00",
              bgcolor: alpha(orange[700] || "#f57c00", 0.06),
            },
          }}
        >
          All Guidelines
        </Button>
        <Button
          component={Link}
          to="/syllabus"
          size="small"
          variant="outlined"
          startIcon={<MenuBookRoundedIcon />}
          endIcon={<ChevronRightRoundedIcon />}
          sx={{
            borderRadius: 2,
            borderColor: alpha(red[700], 0.5),
            color: red[800],
            "&:hover": { borderColor: red[700], bgcolor: alpha(red[700], 0.06) },
          }}
        >
          Full Syllabus
        </Button>
      </Stack>
    </Box>

    {/* Mobile: Tabs */}
    {!isMdUp && (
      <Box sx={{ mb: 1 }}>
        <Tabs
          value={activitiesTab}
          onChange={(_, v) => setActivitiesTab(v)}
          variant="fullWidth"
          textColor="inherit"
          TabIndicatorProps={{
            sx: {
              height: 3,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${red[600]} 0%, ${ORANGE_ACCENT} 100%)`,
            },
          }}
          sx={{
            bgcolor: alpha("#fff", 0.65),
            borderRadius: 2,
            minHeight: 44,
            "& .MuiTab-root": {
              minHeight: 44,
              fontWeight: 800,
              fontSize: 13,
              textTransform: "none",
            },
          }}
        >
          <Tab icon={<CampaignRoundedIcon />} iconPosition="start" label="Notices" />
          <Tab icon={<RuleRoundedIcon />} iconPosition="start" label="Guidelines" />
          <Tab icon={<MenuBookRoundedIcon />} iconPosition="start" label="Syllabus" />
        </Tabs>

        <Box sx={{ mt: 1.5 }}>
          {activitiesTab === 0 && (
            <Box sx={{ p: { xs: 1, sm: 1.25 }, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
              <NoticeComponent />
            </Box>
          )}
          {activitiesTab === 1 && (
            <Box sx={{ p: { xs: 1, sm: 1.25 }, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
              <GuidelineComponent />
            </Box>
          )}
          {activitiesTab === 2 && (
            <Box sx={{ p: { xs: 1, sm: 1.25 }, bgcolor: "background.paper", borderRadius: 2, boxShadow: 1 }}>
              <AcademicSection syllabusRef={syllabusRef} />
            </Box>
          )}
        </Box>
      </Box>
    )}

    {/* Desktop: 3-column grid */}
    {isMdUp && (
      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        <Grid item xs={12} md={6} lg={4}>
          <Box
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 1,
              height: "100%",
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Chip size="small" icon={<CampaignRoundedIcon sx={{ fontSize: 16 }} />} label="Notices" sx={{ fontWeight: 800 }} />
            </Stack>
            <NoticeComponent />
          </Box>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Box
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 1,
              height: "100%",
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Chip size="small" icon={<RuleRoundedIcon sx={{ fontSize: 16 }} />} label="Guidelines" sx={{ fontWeight: 800 }} />
            </Stack>
            <GuidelineComponent />
          </Box>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 1,
              height: "100%",
              border: (t) => `1px solid ${t.palette.divider}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Chip size="small" icon={<MenuBookRoundedIcon sx={{ fontSize: 16 }} />} label="Syllabus" sx={{ fontWeight: 800 }} />
            </Stack>
            <AcademicSection syllabusRef={syllabusRef} />
          </Box>
        </Grid>
      </Grid>
    )}
  </Box>
</Container>


      {/* Syllabus quick links */}



<SyllabusSection/>


      {/* Student Feedback */}
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <SectionHeader
          kicker="Education For Everyone"
          title="Student’s Feedback"
        />
        <Grid container spacing={{ xs: 2, sm: 2.5 }}>
          <Grid item xs={12} md={8}>
            <QuestionAnswers />
          </Grid>
          
        </Grid>
      </Container>

      

      <Footer />
    </>
  );
}

export default HomePage;


// import React, { useState, useRef } from "react";
// import Header from "./header";
// import Footer from "./footer";
// import {
//   Box,
//   Card,
//   CardContent,
//   IconButton,
//   Divider
// } from "@mui/material";
// // import { styled } from "@mui/system";

// import CardMedia from "@mui/material/CardMedia";

// import Typography from "@mui/material/Typography";
// import BookmarkIcon from "@mui/icons-material/Bookmark";

// import QuestionAnswers from "./question_ans";
// import { Link } from "react-router-dom";
// import BannerComponent from "./components/banner";
// import NoticeComponent from "./components/noticeComponent";
// import GuidelineComponent from "./components/guidelines";
// import Syllabus from "./components/Syllabus";

// function HomePage() {
//   // const [items, setItems] = useState([]);

//   // const defaultOptions = {
//   //   loop: true,
//   //   autoplay: true,
//   //   // animationData: animationData,
//   //   rendererSettings: {
//   //     preserveAspectRatio: "xMidYMid slice",
//   //   },
//   // };

//   // const [syllabusList, setSyllabuslist] = useState([
//   //   {
//   //     name: "Physics",
//   //     link: "assets/dummy.pdf",
//   //   },
//   //   {
//   //     name: "Chemistry",
//   //     link: "assets/dummy.pdf",
//   //   },
//   //   {
//   //     name: "Mathematics",
//   //     link: "assets/dummy.pdf",
//   //   },
//   // ]);
//   // const [expanded, setExpanded] = useState(false);
//   // const [selectedId, setSelectedId] = useState(-1);
//   // const handleExpandClick = (orderId) => {
//   //   console.log(orderId);
//   //   if (selectedId === orderId) {
//   //     setSelectedId(-1);
//   //   } else {
//   //     setSelectedId(orderId);
//   //   }
//   // };
//   const syllabusRef = useRef(null);
//   // const StyledCard = styled(Card)({
//   //   display: "flex",
//   //   marginBottom: (theme) => theme.spacing(2),
//   // });

//   // const StyledCardMedia = styled(CardMedia)({
//   //   width: 150,
//   // });

//   // const StyledCardContent = styled(CardContent)({
//   //   flex: "1 0 auto",
//   // });

//   // const StyledBookmarkButton = styled(IconButton)({
//   //   marginLeft: "auto",
//   // });

//   var courseData = [
//     {
//       image: "assets/images/course/science.png",
//       courseName: "Language",
//       courseDesc: ["English (Compulsory)", "Hindi"],
//     },
//     {
//       image: "assets/images/course/science.png",
//       courseName: "Science",
//       courseDesc: [
//         "PCMB (Physics, Chemistry, Mathematics, Biology)",
//         "PCMC (Physics, Chemistry, Mathematics, Computer Science)",
//         "PCME (Physics, Chemistry, Mathematics, Electronics)",
//       ],
//     },
//     {
//       image: "assets/images/course/commerce.jpg",
//       courseName: "Commerce",
//       courseDesc: [
//         "CAMS (Commerce (Business Studies), Accountancy, Basic Mathematics, Statistics)",
//         "CAME (Commerce (Business Studies), Accountancy, Basic Mathematics, Economics)",
//       ],
//     },
//     {
//       image: "assets/images/course/arts.jpg",
//       courseName: "Arts",
//       courseDesc: [
//         " PPES (Psychology, Political Science, Economics, Sociology)",
//         "HESP (History, Economics, Sociology, Political Science)",
//         "HEPP (History, Economics, Political Science, Psychology)",
//       ],
//     },
//   ];
//   var eventData = [
//     {
//       image: "assets/images/event/grid-type-01.jpg",
//       startDate: "11 Jan, 2023",
//       location: "IAC Building",
//       time: "8:00 am - 5:00 pm",
//       eventName: "International Education Fair 2023",
//     },
//     {
//       image: "assets/images/event/grid-type-02.jpg",
//       startDate: "11 Jan, 2023",
//       location: "IAC Building",
//       time: "8:00 am - 5:00 pm",
//       eventName: "International Education Fair 2023",
//     },
//     {
//       image: "assets/images/event/grid-type-03.jpg",
//       startDate: "11 Jan, 2023",
//       location: "IAC Building",
//       time: "8:00 am - 5:00 pm",
//       eventName: "International Education Fair 2023",
//     },
//     {
//       image: "assets/images/event/grid-type-04.jpg",
//       startDate: "11 Jan, 2023",
//       location: "IAC Building",
//       time: "8:00 am - 5:00 pm",
//       eventName: "International Education Fair 2023",
//     },
//     {
//       image: "assets/images/event/grid-type-05.jpg",
//       startDate: "11 Jan, 2023",
//       location: "IAC Building",
//       time: "8:00 am - 5:00 pm",
//       eventName: "International Education Fair 2023",
//     },
//     {
//       image: "assets/images/event/grid-type-06.jpg",
//       startDate: "11 Jan, 2023",
//       location: "IAC Building",
//       time: "8:00 am - 5:00 pm",
//       eventName: "International Education Fair 2023",
//     },
//   ];

//   return (
//     <>
//       <div>
//         <Header />
//         <div>
//           <Box sx={{ pt: "60px" }}>
//             <BannerComponent></BannerComponent>
//           </Box>
//           {/* End Banner Area */}
//           {/* <Grid container spacing={1}>
//             <Grid item xs={6} md={8}>
//               <NoticeComponent></NoticeComponent>
//             </Grid>
//             <Grid item xs={6} md={4}>
//               <GuidelineComponent></GuidelineComponent>
//             </Grid>
//           </Grid> */}
         
// {/* </div> */}
// <Box
//       sx={{
//         pt: 4,
//       }}
//     ></Box>
//           <div className="rbt-course-area bg-color-white pt-12 masonary-wrapper-activation">
//             <div className="container">
//               <div className="row mb--4"> 
//                <div className="col-lg-12">
//                   <div className="section-title text-center">
//                     <span className="subtitle bg-primary-opacity">
//                       Popular Course
//                     </span>
//                     {/* <h2 className="title">
//                       Lessons For <br /> Fast Learning
//                     </h2> */}
//                   </div>
//                 </div>
//               </div>
//               {/* <div className="row">
//                 <div className="col-lg-12">
//                   <ul
//                     className="rbt-portfolio-filter filter-tab-button text-center nav nav-tabs"
//                     id="rbt-myTab"
//                     role="tablist"
//                   >
//                     <li key="course_item_key" className="nav-item" role="presentation">
//                       <button
//                         className="active"
//                         id="all-tab"
//                         data-bs-toggle="tab"
//                         data-bs-target="#all"
//                         type="button"
//                         role="tab"
//                         aria-controls="all"
//                         aria-selected="true"
//                       >
//                         <span className="filter-text">All Course</span>{" "}
//                         <span className="course-number">
//                           0{courseData.length}
//                         </span>
//                       </button>
//                     </li>
//                   </ul>
//                 </div>
//               </div> */}
//               {/* Start Card Area */}
//               <div className="row">
//                 <div className="col-lg-12">
//                   <div className="tab-content mt--60" id="rbt-myTabContent">
//                     <Box
//                       sx={{
//                         display: "grid",
//                         gridTemplateColumns:
//                           "repeat(auto-fit, minmax(250px, 1fr))",
//                         gap: 2,
//                       }}
//                     >
//                       {courseData.map((item, index) => (
//                         <Card
//                           key={index}
//                           sx={{
//                             width: "100%",
//                             height: "100%",
//                             transition: "transform 0.3s, box-shadow 0.3s",
//                             "&:hover": {
//                               transform: "scale(1.05)",
//                               boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
//                             },
//                           }}
//                         >
//                           <CardMedia
//                             component="img"
//                             alt="Card image"
//                             src={item.image}
//                             sx={{ height: "40%" }}
//                           />
//                           <CardContent>
//                             <div className="rbt-card-top">
//                               <IconButton
//                                 className="bookmark-btn"
//                                 title="Bookmark"
//                               >
//                                 <BookmarkIcon />
//                               </IconButton>
//                             </div>
//                             <Typography
//                               variant="h6"
//                               sx={{ fontSize: 20, fontWeight: 800 }}
//                             >
//                               <Link to="#details" className="card-title">
//                                 {item.courseName}
//                               </Link>
//                             </Typography>
//                             <ul className="course-desc">
//                               {item.courseDesc.map((desc, idx) => (
//                                 <li key={idx}>{desc}</li>
//                               ))}
//                             </ul>
//                           </CardContent>
//                         </Card>
//                       ))}
//                     </Box>
//                     {/* </div> */}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <Box
//       sx={{
//         pt: 4,
//       }}
//     ></Box>
//           <div className="row mb--4"> 
//                <div className="col-lg-12">
//                   <div className="section-title text-center">
//                     <span className="subtitle bg-primary-opacity">
//                       Activities
//                     </span>
//                     {/* <h2 className="title">
//                       Lessons For <br /> Fast Learning
//                     </h2> */}
//                   </div>
//                 </div>
//               </div>
//           <Box
//       sx={{
//         pt: 3,
//         display: "flex",
//         flexDirection: { xs: "column", md: "row" },
//         justifyContent: "center",
//         flexWrap: "wrap",
//         gap: 3,
//       }}
//     >
//       <NoticeComponent />
//       <GuidelineComponent />
//       <Syllabus syllabusRef={syllabusRef} />
//     </Box>
//           {/* Start Course Category Area  */}
//           <div className="rbt-course-category rbt-section-gap overflow-hidden">
//             <div className="container">
//               <div className="row mb--25">
//                 <div className="col-lg-12">
//                   <div className="section-title text-center">
//                     <h5 className="subtitle bg-secondary-opacity">
//                       AISECT Inter College Course Syllabus
//                     </h5>
//                     <div className="row">
//                       <div className="col-lg-12">
//                         <ul
//                           className="rbt-portfolio-filter filter-tab-button text-center nav nav-tabs"
//                           id="rbt-myTab"
//                           role="tablist"
//                         >
//                           <li key="syllabus_item_key" className="nav-item" role="presentation">
//                             <button
//                               className="active"
//                               id="all-tab"
//                               data-bs-toggle="tab"
//                               data-bs-target="#all"
//                               type="button"
//                               role="tab"
//                               aria-controls="all"
//                               aria-selected="true"
//                             >
//                               <span className="filter-text">
//                                 Click to Download Syllabus
//                               </span>{" "}
//                             </button>
//                           </li>
//                         </ul>
//                         <Box
//       sx={{pt: 2}}/>
//                         <Divider />
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//               <div className="row" id="syllabus" ref={syllabusRef}>
//                 <div className="col-12 col-lg-12 col-xl-12">
//                   <div className="rbt-categori-leftbar">
//                     <div className="rbt-categori-list">
//                       <a className="blank" href="#!" />
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Physics
//                       </a>
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Chemistry
//                       </a>
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Mathematics
//                       </a>
//                       <a className="blank" href="#!" />
//                     </div>
//                     <div className="rbt-categori-list">
//                       <a className="blank" href="#!" />
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Biology &amp; Mathematics
//                       </a>
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Commerce
//                       </a>
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Arts
//                       </a>
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Economics
//                       </a>
//                       <a className="blank" href="#!" />
//                     </div>
//                     <div className="rbt-categori-list">
//                       <a className="blank" href="#" />
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         English
//                       </a>
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Hindi
//                       </a>
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         History
//                       </a>
//                       <a href="assets/dummy.pdf" target="_blank" rel="noreferrer">
//                         Computer Science
//                       </a>
//                       <a className="blank" href="#" />
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           <div className="rbt-testimonial-area bg-color-white">
//             <div className="container">
//               <div className="row">
//                 <div className="col-lg-12 mb--60">
//                   <div className="section-title text-center">
//                     <span className="subtitle bg-coral-opacity">
//                       EDUCATION FOR EVERYONE
//                     </span>
//                     <h2 className="title">Student's Feedback</h2>
//                   </div>
//                 </div>
//               </div>
//               <div className="row g-5">
//                 <div className="col-lg-6">
//                   <QuestionAnswers></QuestionAnswers>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <Box
//       sx={{pt: 2}}>

// <section class="deskbg">
//     <div class="container">
//         <div class="desklist">
//             <ul>
//                 <li>
//                     <div class="deskbox">
//                         <div class="deskimg wow fadeInLeft"><img src="resources/assets/images/drdksingh.jpg" alt="Prof. (Dr.) Dinesh Kumar Singh" /></div>
//                         <div class="deskcont wow fadeInUp">
//                             <h3>From Principal's Desk</h3>
//                             <h4>Shree Mamta Sharma</h4>
//                             <p>AISECT Inter College, Hazaribag</p>
//                             <a href="#" class="morebtn">Read more</a>
//                         </div>
//                     </div>
//                 </li>
//                 <li>
//                     <div class="deskbox">
//                         <div class="deskimg wow fadeInRight"><img src="resources/assets/img/admin/akhilesh_fa.jpg" alt="Sri Akhilesh Sharma" /></div>
//                         <div class="deskcont wow fadeInUp">
//                             <h3>From VP's Desk</h3>
//                             <h4> Sri Arjun</h4>
//                             <p><a href="mailto:fa@vbu.ac.in"><i class="fa fa-envelope"></i> fa@vbu.ac.in</a></p>
//                             <a href="#" class="morebtn">Read more</a>
//                         </div>
//                     </div>
//                 </li>
//                 <li>
//                     <div class="deskbox">
//                         <div class="deskimg wow fadeInLeft"><img src="resources/assets/img/admin/index.html" alt="" /></div>
//                         <div class="deskcont wow fadeInUp">
//                             <h3>From DSW's Desk </h3>
//                             <h4></h4>
//                             <p><a href="mailto:dsw@vbu.ac.in"><i class="fa fa-envelope"></i> </a></p>
//                             <a href="#" class="morebtn">Read more</a>
//                         </div>
//                     </div>
//                 </li>
//                 <li>
//                     <div class="deskbox">
//                         <div class="deskimg wow fadeInRight"><img src="resources/assets/img/admin/index.html" alt="" /></div>
//                         <div class="deskcont wow fadeInUp">
//                             <h3>From CCDC’s Desk</h3>
//                             <h4></h4>
//                             <p><a href="mailto:ccdc@vbu.ac.in"><i class="fa fa-envelope"></i> </a></p>
//                             <a href="#" class="morebtn">Read more</a>
//                         </div>
//                     </div>
//                 </li>

//             </ul>
//         </div>
//     </div>
// </section>


// <section class="aboutbg">
//     <div class="container">
//         <div class="aboutcont">
//             <h2 class="heading2 wow fadeInUp"><span>About</span> AISECT INTER COLLEGE</h2>
//             <p class="wow fadeInUp">AISECT Inter College (AIU) was established in the year 2023, with its campus located at Matwari, Hazaribagh — a prominent area of the North Chhotanagpur division in the state of Jharkhand. The college is situated approximately 100 kilometers from Ranchi, the capital city of Jharkhand.

// AIU is committed to delivering quality education and holistic development for intermediate-level students. With a strong focus on academic excellence, discipline, and skill-based learning, the institution aims to prepare students for higher education and future professional challenges. The college emphasizes a learner-centric approach, fostering growth across Science, Arts, and Commerce streams with the support of experienced faculty and modern infrastructure.</p>
//             <a href="about_us.html" class="morelink">Read more</a>
//         </div>
//     </div>
// </section>
//       </Box>
      
//           <Box
//       sx={{pt: 2}}/>
//           <div className="rbt-event-area bg-color-extra2 ">
//             <div className="container">
//             <Box
//       sx={{pt: 6}}/>
//               <div className="row mb--60">
//                 <div className="col-lg-12">
//                   <div className="section-title text-center">
//                     <span className="subtitle bg-primary-opacity">
//                       OUR EVENTS
//                     </span>
//                     <h2 className="title">
//                       AISECT Inter College Upcoming Events
//                     </h2>
//                   </div>
//                 </div>
//               </div>
//               <div className="row g-5">
//                 {/* Start Single Event  */}
//                 {eventData.map((items, index) => (
//                   <div className="col-lg-6 col-md-6 col-sm-6 col-12">
//                     <div
//                       key={index}
//                       className="rbt-card card-list-2 event-list-card variation-01 rbt-hover"
//                     >
//                       <div className="rbt-card-img">
//                         <a href="!#">
//                           <img key={index+2} src={items.image} alt="Card image" />
//                         </a>
//                       </div>
//                       <div className="rbt-card-body">
//                         <ul className="rbt-meta">
//                           <li key={index+3}>
//                             <i className="feather-calendar" />
//                             {items.startDate}
//                           </li>
//                           <li  key={index+1}>
//                             <i className="feather-map-pin" />
//                             {items.location}
//                           </li>
//                         </ul>
//                         <h4 className="rbt-card-title">
//                           <a href="#!">{items.eventName}</a>
//                         </h4>
//                         <div className="read-more-btn">
//                           <a
//                             className="rbt-btn btn-border hover-icon-reverse btn-sm radius-round"
//                             href="#"
//                           >
//                             <span className="icon-reverse-wrapper">
//                               <span className="btn-text">Get Ticket</span>
//                               <span className="btn-icon">
//                                 <i className="feather-arrow-right" />
//                               </span>
//                               <span className="btn-icon">
//                                 <i className="feather-arrow-right" />
//                               </span>
//                             </span>
//                           </a>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//                 {/* End Single Event  */}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <Box sx={{ pb: "30px" }}></Box>
//       <Footer />
//     </>
//   );
// }

// export default HomePage;
